import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'nexo-menu-favorites';

/**
 * Hook for managing starred/favorite menu items
 * Favorites are stored in localStorage and persist across sessions
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Sync to localStorage whenever favorites change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch (error) {
      console.warn('Failed to save favorites to localStorage:', error);
    }
  }, [favorites]);

  /**
   * Add an item to favorites
   */
  const addFavorite = useCallback((itemId: string) => {
    setFavorites(prev => {
      if (prev.includes(itemId)) return prev;
      return [...prev, itemId];
    });
  }, []);

  /**
   * Remove an item from favorites
   */
  const removeFavorite = useCallback((itemId: string) => {
    setFavorites(prev => prev.filter(id => id !== itemId));
  }, []);

  /**
   * Toggle an item's favorite status
   */
  const toggleFavorite = useCallback((itemId: string) => {
    setFavorites(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });
  }, []);

  /**
   * Check if an item is a favorite
   */
  const isFavorite = useCallback((itemId: string) => {
    return favorites.includes(itemId);
  }, [favorites]);

  /**
   * Clear all favorites
   */
  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  /**
   * Reorder favorites (for drag-and-drop)
   */
  const reorderFavorites = useCallback((newOrder: string[]) => {
    setFavorites(newOrder);
  }, []);

  return {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    reorderFavorites,
  };
}

export type UseFavoritesReturn = ReturnType<typeof useFavorites>;
